import { FC, useLayoutEffect, useRef, useState } from 'react';
import {
  Paper,
  Text,
  Group,
  ActionIcon,
  Textarea,
  Stack,
  Loader,
  Anchor,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconTrash, IconCheck, IconPencil } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PatientAnnotationNote } from './usePatientNotes';
import { useMedplumProfile } from '@medplum/react';
import { formatHumanName } from '@medplum/core';
import DeleteConfirmModal from '@/pages/ainigma/patient/images-tab/DeleteModal';

// -----------------------------------------------------------------------------
interface NoteItemProps {
  note: PatientAnnotationNote;
  isEditing: boolean;
  isSaving?: boolean;
  editingContent?: string;
  onContentChange: (content: string) => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onDelete: () => void;
}

// -----------------------------------------------------------------------------
export const NoteItem: FC<NoteItemProps> = ({
  note,
  isEditing,
  isSaving = false,
  editingContent,
  onContentChange,
  onStartEditing,
  onStopEditing,
  onDelete,
}) => {
  const { t } = useTranslation(['patient-pages', 'common']);
  const profile = useMedplumProfile();
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  const formattedDate = dayjs(note.timestamp).format('DD/MM/YYYY HH:mm');

  // Content to display (use editingContent if editing, otherwise note.content)
  const displayContent = isEditing
    ? (editingContent ?? note.content)
    : note.content;

  useLayoutEffect(() => {
    if (isEditing) {
      return;
    }

    const el = contentRef.current;
    if (!el) {
      return;
    }

    // If content is taller than the clamped line, it is truncated
    setIsOverflowing(el.scrollHeight > el.clientHeight);
  }, [displayContent, isEditing]);

  // ---------------------------------------------------------------------------
  return (
    <>
      <Paper
        withBorder
        p="md"
        radius="md"
        bg={isEditing ? 'blue.0' : 'transparent'}
      >
        <Stack gap={4}>
          {/* Header */}
          <Group justify="space-between">
            <Group gap="sm">
              <Text size="sm" fw={500}>
                {note.authorName || formatHumanName(profile?.name?.[0])}
              </Text>
              <Text size="xs" c="dimmed">
                {formattedDate}
              </Text>
            </Group>

            <Group gap="xs">
              {isEditing ? (
                <ActionIcon
                  variant="subtle"
                  color="green"
                  onClick={onStopEditing}
                  aria-label="Done editing"
                >
                  <IconCheck size={16} />
                </ActionIcon>
              ) : (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={onStartEditing}
                  aria-label="Edit note"
                >
                  <IconPencil size={16} />
                </ActionIcon>
              )}
              {note.id && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={openDeleteModal}
                  aria-label="Delete note"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Group>
          </Group>

          {/* Content */}
          {isEditing ? (
            <Textarea
              value={displayContent}
              onChange={(e) => onContentChange(e.target.value)}
              minRows={4}
              maxRows={10}
              autosize
              autoFocus
              onFocus={(e) => {
                // Move cursor to the end of the text
                const len = e.target.value.length;
                e.target.setSelectionRange(len, len);
              }}
              placeholder={t('notes.placeholder', {
                ns: 'patient-pages',
                defaultValue: 'Enter note content...',
              })}
              rightSection={isSaving ? <Loader size="xs" /> : null}
            />
          ) : (
            <>
              <Text
                ref={contentRef}
                size="sm"
                lineClamp={expanded ? undefined : 2}
                c="gray.9"
              >
                {displayContent || (
                  <Text c="dimmed" fs="italic">
                    {t('notes.emptyNote', {
                      ns: 'patient-pages',
                      defaultValue: 'Empty note',
                    })}
                  </Text>
                )}
              </Text>
              {isOverflowing && (
                <Anchor
                  onClick={() => setExpanded((v) => !v)}
                  c="gray"
                  size="xs"
                >
                  {expanded
                    ? t('notes.less', {
                        ns: 'patient-pages',
                        defaultValue: '- Less',
                      })
                    : t('notes.more', {
                        ns: 'patient-pages',
                        defaultValue: '+ More',
                      })}
                </Anchor>
              )}
            </>
          )}
        </Stack>
      </Paper>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
        title={t('notes.deleteTitle', {
          ns: 'patient-pages',
          defaultValue: 'Delete Note',
        })}
        message={t('notes.deleteMessage', {
          ns: 'patient-pages',
          defaultValue:
            'Are you sure you want to delete this note? This action cannot be undone.',
        })}
      />
    </>
  );
};
